/* =============================================
   THE ONE TEE — Custom JavaScript
   Loader · Cursor · Scroll Reveal · Word Reveal · Horizontal Scroll
   ============================================= */

(function () {
  'use strict';

  // =============================================
  // LOADER — two-phase: word mark → logo mark → fade out
  // =============================================
  const loader = document.getElementById('tot-loader');

  // The inline script in theme.liquid hides the loader on same-day revisits
  // (once-per-day). Only run the animation when it is actually visible.
  if (loader && loader.style.display !== 'none') {
    // Wait for the Glacial Indifference font to be ready before kicking off
    // the animation timeline — otherwise FOIT hides the letters while the
    // per-letter animation-delays tick away in the background.
    const startLoader = () => {
      loader.classList.add('tot-loader--start');
      // Phase 1 (logo):             0.0s — 1.8s  (pop → stroke draw → short pulse → fade)
      // Phase 2 (text, per-letter): 1.75s — 3.25s (T-H-E / O-N-E / T-E-E)
      // Hide overlay:               clean fade starts at 3.35s, fully hidden at 3.95s (0.55s fade)
      setTimeout(() => loader.classList.add('hide'), 3350);
      setTimeout(() => { loader.style.display = 'none'; }, 3950);
    };

    // Kick off on next frame so the layout is stable; don't wait for the
    // webfont — the paused/unpaused class-gated animations mean delays only
    // begin counting now, and the fallback font will swap in seamlessly.
    requestAnimationFrame(() => {
      requestAnimationFrame(startLoader);
    });
  }

  // =============================================
  // CUSTOM CURSOR
  // =============================================
  const cursor = document.getElementById('tot-cursor');
  const cursorDot = document.getElementById('tot-cursor-dot');

  if (cursor && cursorDot && window.matchMedia('(pointer: fine)').matches) {
    let mouseX = 0, mouseY = 0;
    let cursorX = 0, cursorY = 0;
    let dotX = 0, dotY = 0;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    });

    function animateCursor() {
      cursorX += (mouseX - cursorX) * 0.12;
      cursorY += (mouseY - cursorY) * 0.12;
      dotX += (mouseX - dotX) * 0.25;
      dotY += (mouseY - dotY) * 0.25;

      cursor.style.left = (cursorX - 10) + 'px';
      cursor.style.top = (cursorY - 10) + 'px';
      cursorDot.style.left = (dotX - 3) + 'px';
      cursorDot.style.top = (dotY - 3) + 'px';

      requestAnimationFrame(animateCursor);
    }
    animateCursor();

    // Hover effects — attach to interactive elements
    function attachCursorHover() {
      document.querySelectorAll('a, button, .tot-btn, .tot-btn-pack, .tot-pack-card, .tot-h-card').forEach((el) => {
        el.addEventListener('mouseenter', () => cursor.classList.add('hover'));
        el.addEventListener('mouseleave', () => cursor.classList.remove('hover'));
      });
    }
    attachCursorHover();

    // Re-attach after Shopify section updates in editor
    document.addEventListener('shopify:section:load', attachCursorHover);
  }

  // =============================================
  // SCROLL REVEAL
  // =============================================
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
      }
    });
  }, { threshold: 0.15 });

  function initReveal() {
    document.querySelectorAll('.tot-reveal').forEach((el) => revealObserver.observe(el));
  }
  initReveal();
  document.addEventListener('shopify:section:load', initReveal);

  // =============================================
  // STATEMENT — Word-by-word reveal on scroll
  // =============================================
  function initStatement() {
    const statementSection = document.querySelector('.tot-statement');
    const statementEl = document.querySelector('.tot-statement-text');
    if (!statementSection || !statementEl) return;

    if (statementEl.dataset.parsed) return;
    statementEl.dataset.parsed = '1';

    let wordIndex = 0;

    function wrapTextNode(node) {
      const text = node.textContent;
      if (!text.trim()) return;
      const parts = text.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      parts.forEach((part) => {
        if (!part.trim()) {
          frag.appendChild(document.createTextNode(part));
        } else {
          const span = document.createElement('span');
          span.className = 'word';
          span.dataset.idx = wordIndex++;
          span.textContent = part;
          frag.appendChild(span);
        }
      });
      node.parentNode.replaceChild(frag, node);
    }

    function wrapHighlightSpan(span) {
      const words = span.textContent.trim().split(/\s+/);
      const frag = document.createDocumentFragment();
      words.forEach((w, i) => {
        const s = document.createElement('span');
        s.className = 'word highlight';
        s.dataset.idx = wordIndex++;
        s.textContent = w;
        frag.appendChild(s);
        if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
      });
      span.parentNode.replaceChild(frag, span);
    }

    // Process highlight spans first, then plain text nodes
    statementEl.querySelectorAll('.highlight').forEach(wrapHighlightSpan);

    const walker = document.createTreeWalker(statementEl, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    let n;
    while ((n = walker.nextNode())) {
      // Skip text nodes already inside a .word span
      if (!n.parentNode.closest('.word')) textNodes.push(n);
    }
    textNodes.forEach(wrapTextNode);

    const statementWords = statementEl.querySelectorAll('.word');
    const totalWords = statementWords.length;

    function updateStatement() {
      const rect = statementSection.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
      const activeCount = Math.floor(progress * totalWords * 1.8);
      statementWords.forEach((word, i) => {
        word.classList.toggle('active', i < activeCount);
      });
    }

    window.addEventListener('scroll', updateStatement, { passive: true });
    updateStatement();
  }

  // =============================================
  // HORIZONTAL SCROLL
  // =============================================
  function initHorizontalScroll() {
    const hSection = document.querySelector('.tot-horizontal-section');
    const hTrack = document.querySelector('.tot-horizontal-track');
    if (!hSection || !hTrack) return;

    function updateHorizontalScroll() {
      const rect = hSection.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, -rect.top / (rect.height - window.innerHeight)));
      const totalScroll = hTrack.scrollWidth - window.innerWidth;
      hTrack.style.transform = `translateX(${-progress * totalScroll}px)`;
    }

    window.addEventListener('scroll', updateHorizontalScroll, { passive: true });
    updateHorizontalScroll();
  }

  // =============================================
  // SMOOTH ANCHOR SCROLLING
  // =============================================
  function initAnchorScroll() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', function (e) {
        const href = this.getAttribute('href');
        if (href === '#') return;
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  // =============================================
  // INIT ON DOM READY
  // =============================================
  function init() {
    initStatement();
    initHorizontalScroll();
    initAnchorScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify editor section loads
  document.addEventListener('shopify:section:load', init);

})();
